import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Modal, Descriptions, Tabs, message, Button, Input, List } from 'antd';
import { useSelector } from 'react-redux';
import { STATUSES, columnLabels } from '../../constants/taskColumnLabels';
import StatusTag from '../StatusTag';
import { canManageTaskStatus } from '../../utils/taskPermissions';
import { tasksApi } from '../../services/tasks';

const { TabPane } = Tabs;
const { TextArea } = Input;


const CustomComment = ({ item, users }) => {
  const user = users?.find(u => u.id === item.created_by);
  const authorName = user ? `${user.first_name} ${user.last_name}` : 'Неизвестный пользователь';

  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ fontWeight: 'bold' }}>{authorName}</div>
      <div style={{ margin: '4px 0' }}>{item.comment}</div>
      <div style={{ fontSize: 12, color: '#999' }}>
        {new Date(item.created_at).toLocaleDateString('ru-RU')}
      </div>
    </div>
  );
};

const TaskDetails = ({ visible, onClose, task, onCommentSubmit, users, candidates, onEditTask, fetchTaskHistory }) => {
  const { taskHistory, isLoading: historyLoading } = useSelector((state) => state.tasks);
  const { role: userRole } = useSelector((state) => state.auth);

  const [comment, setComment] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeResult, setCompleteResult] = useState('');
  const [taskFiles, setTaskFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);

  const getEntityName = (id, entities) => {
    if (!entities || !Array.isArray(entities)) return 'Не указан';
    const entity = entities.find(e => e.id === id);
    return entity ? entity.full_name || `${entity.first_name} ${entity.last_name}` : 'Не указан';
  };

  const getVacancyName = (task) => {
    if (task.vacancy_name) {
      return task.vacancy_name;
    }

    if (task.vacancy && task.vacancy.job_title) {
      return task.vacancy.job_title;
    }
    return 'Не указано';
  };

  const getFieldDisplayName = useCallback((fieldName) => {
    return columnLabels[fieldName] || fieldName;
  }, []);

  const handleCommentSubmit = useCallback(async () => {
    if (!comment.trim()) {
      message.warning('Введите комментарий перед отправкой');
      return;
    }
    if (!task?.id) {
      message.error('Задача не выбрана');
      return;
    }
    try {
      if (onCommentSubmit) {
        await onCommentSubmit(task.id, comment);
      }
      setComment('');
      await fetchTaskHistory(task.id);
    } catch (error) {
      message.error('Ошибка при отправке комментария');
      console.error(error);
    }
  }, [comment, task, onCommentSubmit]);

  const handleStartTask = useCallback(async () => {
    if (!task?.id || !onEditTask) return;

    try {
      await onEditTask(task.id, { status: 'in_progress' });
      message.success('Задача начата');
      await fetchTaskHistory(task.id);
      onClose();
    } catch (error) {
      message.error('Ошибка при начале выполнения задачи');
      console.error(error);
    }
  }, [task, onEditTask, onClose]);

  const handleCompleteTask = useCallback(async () => {
    if (!task?.id || !onEditTask) return;

    // Если result_required = true, проверяем что результат заполнен
    if (task.result_required && !completeResult.trim()) {
      message.warning('Необходимо указать результат выполнения задачи');
      return;
    }

    try {
      const updateData = { status: 'closed' };
      if (completeResult.trim()) {
        updateData.result = completeResult.trim();
      }

      await onEditTask(task.id, updateData);
      setShowCompleteModal(false);
      setCompleteResult('');
      await fetchTaskHistory(task.id);
      onClose();
    } catch (error) {
      message.error('Ошибка при закрытии задачи');
      console.error(error);
    }
  }, [task, onEditTask, completeResult, onClose]);


  const canManageTask = useMemo(() => {
    if (!task) return false;
    const currentUserId = parseInt(localStorage.getItem('user_id'));
    if (task.observer_id && currentUserId === task.observer_id) return false;
    return canManageTaskStatus(task, currentUserId, userRole);
  }, [task, userRole]);

  const canEditTask = useMemo(() => {
    if (!task) return false;
    const currentUserId = parseInt(localStorage.getItem('user_id'));
    return currentUserId === task.creator_id;
  }, [task]);

  const canAddComment = useMemo(() => {
    if (!task) return false;
    const currentUserId = parseInt(localStorage.getItem('user_id'));
    return currentUserId === task.assignee_id ||
      currentUserId === task.co_assignee_id ||
      currentUserId === task.observer_id ||
      currentUserId === task.creator_id;
  }, [task]);

  useEffect(() => {
    const loadFiles = async () => {
      if (!visible || !task?.id) return;
      try {
        setFilesLoading(true);
        const files = await tasksApi.getTaskFiles(task.id);
        setTaskFiles(Array.isArray(files) ? files : []);
      } catch (e) {
        setTaskFiles([]);
      } finally {
        setFilesLoading(false);
      }
    };
    loadFiles();
  }, [visible, task?.id]);

  const comments = useMemo(() => {
    if (!Array.isArray(taskHistory?.comments)) return [];
    return taskHistory?.comments?.filter(item => item.comment);
  }, [taskHistory]);

  const historyItems = useMemo(() => {
    if (!taskHistory || typeof taskHistory !== 'object' || !task) return [];

    const items = [];

    Object.entries(taskHistory).forEach(([fieldName, fieldHistory]) => {
      if (fieldName === 'comments' || !Array.isArray(fieldHistory)) return;

      const sortedHistory = [...fieldHistory].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      sortedHistory.forEach((item, index) => {
        const originalFieldName = fieldName;
        const displayFieldName = getFieldDisplayName(fieldName);

        if (item.previous_value === false || item.previous_value === 'false') {
          return;
        }

        const formatValue = (val) => {
          if (fieldName === 'assignee_id' && val) {
            const user = users?.find(u => u.id === val || u.id === parseInt(val));
            return user ? `${user.first_name} ${user.last_name}` : `ID: ${val}`;
          }
          if (fieldName === 'co_assignee_id' && val) {
            const user = users?.find(u => u.id === val || u.id === parseInt(val));
            return user ? `${user.first_name} ${user.last_name}` : `ID: ${val}`;
          }
          if (fieldName === 'observer_id' && val) {
            const user = users?.find(u => u.id === val || u.id === parseInt(val));
            return user ? `${user.first_name} ${user.last_name}` : `ID: ${val}`;
          }
          if (fieldName === 'candidate_id' && val) {
            const candidate = candidates?.find(c => c.id === val || c.id === parseInt(val));
            return candidate ? candidate.full_name : `ID: ${val}`;
          }
          if (fieldName === 'status' && val) {
            return val; // Возвращаем значение для последующего рендеринга через StatusTag
          }
          if (fieldName === 'deadline' && val) {
            return new Date(val).toLocaleDateString('ru-RU');
          }
          return val || 'Не указано';
        };

        let currentValue;
        if (index === sortedHistory.length - 1) {
          currentValue = task[fieldName];
        } else {
          currentValue = sortedHistory[index + 1].previous_value;
        }

        items.push({
          ...item,
          field_name: displayFieldName,
          original_field_name: originalFieldName,
          previous_value: formatValue(item.previous_value === 'None' ? null : item.previous_value),
          current_value: formatValue(currentValue),
          is_initial_setup: item.previous_value === 'None'
        });
      });
    });

    return items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [taskHistory, getFieldDisplayName, task, users, candidates]);

  const renderHistoryItem = useCallback((item) => {
    const user = users?.find(u => u.id === item.created_by);
    const authorName = user ? `${user.first_name} ${user.last_name}` : 'Неизвестный пользователь';

    if (item.original_field_name === 'files') {
      return (
        <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div>
            <strong>{authorName}</strong>{item.comment ? `: ${item.comment}` : ': Изменения по файлам'}
          </div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
            {new Date(item.created_at).toLocaleDateString('ru-RU')}
          </div>
        </div>
      );
    }

    const renderValue = (value, fieldName) => {
      if (fieldName === 'Исполнитель' && value) {
        const user = users?.find(u => u.id === value || u.id === parseInt(value));
        return user ? `${user.first_name} ${user.last_name}` : `${value}`;
      }
      if (fieldName === 'Соисполнитель' && value) {
        const user = users?.find(u => u.id === value || u.id === parseInt(value));
        return user ? `${user.first_name} ${user.last_name}` : `${value}`;
      }
      if (fieldName === 'Наблюдатель' && value) {
        const user = users?.find(u => u.id === value || u.id === parseInt(value));
        return user ? `${user.first_name} ${user.last_name}` : `${value}`;
      }
      if (fieldName === 'Кандидат' && value) {
        const candidate = candidates?.find(c => c.id === value || c.id === parseInt(value));
        return candidate ? candidate.full_name : `${value}`;
      }
      if (fieldName === 'Статус' && value) {
        return <StatusTag status={value} statuses={STATUSES} />;
      }
      if (fieldName === 'Срок выполнения' && value) {
        return new Date(value).toLocaleDateString('ru-RU');
      }
      return value || 'Не указано';
    };

    return (
      <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
        <div>
          <strong>{authorName}</strong> изменил поле: <strong>{item.field_name}</strong>
        </div>
        <div style={{ marginTop: 4 }}>
          {item.is_initial_setup ? (
            <span style={{ color: '#999' }}>Установлено значение: </span>
          ) : (
            <>
              <span style={{ color: '#999' }}>Было: </span>
              {renderValue(item.previous_value, item.field_name)}
              <span style={{ margin: '0 8px' }}>→</span>
            </>
          )}
          <span style={{ fontWeight: 'bold' }}>{renderValue(item.current_value, item.field_name)}</span>
        </div>
        {item.comment && (
          <div style={{ marginTop: 4, fontStyle: 'italic', color: '#666' }}>
            Комментарий: {item.comment}
          </div>
        )}
        <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
          {new Date(item.created_at).toLocaleDateString('ru-RU')}
        </div>
      </div>
    );
  }, [users, candidates]);

  if (!task) return null;

  return (
    <Modal
      title="Детали задачи"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
    >
      <Tabs
        defaultActiveKey="1"
        tabBarExtraContent={
          (canManageTask || canEditTask) ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              {canManageTask && task.status === 'new' && (
                <Button
                  type="primary"
                  size="small"
                  onClick={handleStartTask}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  Начать выполнение
                </Button>
              )}
              {canManageTask && task.status === 'in_progress' && (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    if (task.result_required) {
                      setShowCompleteModal(true);
                    } else {
                      handleCompleteTask();
                    }
                  }}
                  style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                >
                  Завершить
                </Button>
              )}
            </div>
          ) : null
        }
      >
        <TabPane tab="Общая информация" key="1">
          <Descriptions bordered column={2}>
            <Descriptions.Item label="ID">{task.id}</Descriptions.Item>
            <Descriptions.Item label="Статус">
              <StatusTag status={task.status} statuses={STATUSES} />
            </Descriptions.Item>
            <Descriptions.Item label="Описание" span={2}>
              {task.description || 'Не указано'}
            </Descriptions.Item>
            <Descriptions.Item label="Срок выполнения">
              {task.deadline ? new Date(task.deadline).toLocaleDateString() : 'Не указано'}
            </Descriptions.Item>
            <Descriptions.Item label="Кандидат">
              {task.candidate && task.candidate.full_name
                ? task.candidate.full_name
                : getEntityName(task.candidate_id, candidates)}
            </Descriptions.Item>
            <Descriptions.Item label="Вакансия">
              {getVacancyName(task)}
            </Descriptions.Item>
            <Descriptions.Item label="Исполнитель">
              {getEntityName(task.assignee_id, users)}
            </Descriptions.Item>
            <Descriptions.Item label="Соисполнитель">
              {getEntityName(task.co_assignee_id, users)}
            </Descriptions.Item>
            <Descriptions.Item label="Наблюдатель">
              {getEntityName(task.observer_id, users)}
            </Descriptions.Item>
            <Descriptions.Item label="Прикрепленные файлы" span={2}>
              {filesLoading
                ? 'Загрузка...'
                : (taskFiles && taskFiles.length > 0
                  ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {taskFiles.map(f => (
                        <li key={f.id || f.name}>{f.name || 'Файл'}</li>
                      ))}
                    </ul>
                  )
                  : 'Нет файлов')}
            </Descriptions.Item>
            {task.result_required && (
              <Descriptions.Item label="Результат">
                {(() => {
                  if (task.status === 'new') {
                    return <span style={{ color: '#999', fontStyle: 'italic' }}>Задача еще не в работе</span>;
                  } else if (task.status === 'in_progress') {
                    return <span style={{ color: '#1890ff', fontStyle: 'italic' }}>Ожидается результат</span>;
                  } else if (task.status === 'closed') {
                    return task.result ? (
                      <span style={{ fontWeight: 'bold' }}>
                        {task.result}
                      </span>
                    ) : (
                      <span style={{ color: '#999', fontStyle: 'italic' }}>Результат не указан</span>
                    );
                  } else {
                    return <span style={{ color: '#999', fontStyle: 'italic' }}>Результат не указан</span>;
                  }
                })()}
              </Descriptions.Item>
            )}
          </Descriptions>
        </TabPane>

        <TabPane tab="История" key="history" loading={historyLoading}>
          {historyItems.length === 0 ? <p>Нет истории</p> :
            <List
              dataSource={historyItems}
              renderItem={renderHistoryItem}
            />
          }
        </TabPane>

        <TabPane tab="Комментарии" key="comments" loading={historyLoading}>
          {comments && comments.length > 0 ? (
            <List
              className="comment-list"
              header={`${comments.length} комментария(ев)`}
              itemLayout="horizontal"
              dataSource={comments}
              renderItem={item => <CustomComment item={item} users={users} />}
            />
          ) : (
            <p>Нет комментариев</p>
          )}
        </TabPane>
      </Tabs>

      {canAddComment && (
        <div style={{ marginTop: 24 }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>
            Ваш комментарий
          </label>
          <TextArea
            rows={4}
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Оставьте комментарий..."
          />
          <Button
            type="primary"
            onClick={handleCommentSubmit}
            style={{ marginTop: 12 }}
          >
            Отправить комментарий
          </Button>
        </div>
      )}

      {task?.result_required && (
        <Modal
          title="Завершение задачи"
          open={showCompleteModal}
          onCancel={() => {
            setShowCompleteModal(false);
            setCompleteResult('');
          }}
          onOk={handleCompleteTask}
          okText="Завершить"
          cancelText="Отмена"
          okButtonProps={{
            disabled: task?.result_required && !completeResult.trim(),
            style: task?.result_required && !completeResult.trim()
              ? {
                backgroundColor: '#f5f5f5',
                borderColor: '#d9d9d9',
                color: '#bfbfbf',
                cursor: 'not-allowed'
              }
              : { backgroundColor: '#1890ff', borderColor: '#1890ff' }
          }}
        >
          <div style={{ padding: '16px 0' }}>
            <p style={{ marginBottom: 16, color: '#666' }}>
              Опишите результат выполнения задачи:
            </p>
            <TextArea
              rows={4}
              placeholder="Введите описание результата..."
              value={completeResult}
              onChange={(e) => setCompleteResult(e.target.value)}
            />
          </div>
        </Modal>
      )}
    </Modal>
  );
};

export default React.memo(TaskDetails);